/*
 * Shop-scoped pin tip. Shopify never tells an embedded app whether the
 * merchant pinned it, so this file is the only memory of that choice.
 *
 * localStorage key: ifh_pin_app_prompt:${shop_name}
 *   { firstRunSeen, reminderCount, dismissCount, lastShownAt, pinned }
 *
 * sessionStorage keeps one appearance up across in-app navigations and
 * refresh in the same tab. A new tab does not see that flag, so it will
 * not show the card again until 7 days have passed, and it will not add
 * another reminder count.
 *
 * Example (first run, progress strip on screen):
 *   resolvePinAppBanner({ shopName: "shop.myshopify.com", progressBarVisible: true, jobStatusLoaded: true, state: EMPTY, sessionActive: false })
 *   → { visible: true, placement: "beside", record: "spotlight" }
 *
 * Example (same tab after that write, including a refresh):
 *   → { visible: true, placement: "beside", record: null }
 *
 * Example (new tab the next day):
 *   → { visible: false, placement: null, record: null }
 */

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const EMPTY_STATE = Object.freeze({
  firstRunSeen: false,
  reminderCount: 0,
  dismissCount: 0,
  lastShownAt: null,
  pinned: false,
});

const localKey = (shopName) => `ifh_pin_app_prompt:${shopName}`;
const sessionKey = (shopName) => `ifh_pin_app_prompt_session:${shopName}`;

const defaultLocalStorage = () =>
  typeof localStorage === "undefined" ? null : localStorage;

const defaultSessionStorage = () =>
  typeof sessionStorage === "undefined" ? null : sessionStorage;

const safeGet = (storage, key) => {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSet = (storage, key, value) => {
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
};

const safeRemove = (storage, key) => {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch (e) {
    // Storage can throw in private mode. Hiding the card still works for this render.
  }
};

const asCount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
};

const normalizeState = (raw) => {
  if (!raw || typeof raw !== "object") {
    return { ...EMPTY_STATE };
  }
  return {
    firstRunSeen: raw.firstRunSeen === true,
    reminderCount: asCount(raw.reminderCount),
    dismissCount: asCount(raw.dismissCount),
    lastShownAt: typeof raw.lastShownAt === "string" ? raw.lastShownAt : null,
    pinned: raw.pinned === true,
  };
};

export const readPinAppBannerState = (shopName, storage = defaultLocalStorage()) => {
  if (!shopName) return { ...EMPTY_STATE };
  const raw = safeGet(storage, localKey(shopName));
  if (!raw) return { ...EMPTY_STATE };
  try {
    return normalizeState(JSON.parse(raw));
  } catch (e) {
    return { ...EMPTY_STATE };
  }
};

const writePinAppBannerState = (shopName, state, storage) => {
  safeSet(storage, localKey(shopName), JSON.stringify(normalizeState(state)));
};

export const isPinAppBannerSessionActive = (
  shopName,
  storage = defaultSessionStorage()
) => {
  if (!shopName) return false;
  return safeGet(storage, sessionKey(shopName)) === "1";
};

const clearPinAppBannerSession = (shopName, storage) => {
  if (!shopName) return;
  safeRemove(storage, sessionKey(shopName));
};

/*
 * Last-shown time is missing or unreadable → the gap has already elapsed.
 * Example: lastShownAt null → true (first compact reminder can show now).
 * Example: lastShownAt 2 days ago → false.
 * Example: lastShownAt 8 days ago → true.
 */
const reminderGapElapsed = (lastShownAt, now) => {
  if (!lastShownAt) return true;
  const shownAt = Date.parse(lastShownAt);
  if (Number.isNaN(shownAt)) return true;
  return now - shownAt >= SEVEN_DAYS_MS;
};

const hidden = () => ({ visible: false, placement: null, record: null });

/**
 * @returns {{ visible: boolean, placement: "beside" | "compact" | null, record: "spotlight" | "compact" | null }}
 */
export const resolvePinAppBanner = ({
  shopName,
  progressBarVisible,
  jobStatusLoaded,
  now = Date.now(),
  state,
  sessionActive,
}) => {
  if (!shopName || !jobStatusLoaded) return hidden();

  const current = normalizeState(state);

  // Pinned, or dismissed twice, never comes back. X is not the same as pinned.
  if (current.pinned || current.dismissCount >= 2) return hidden();

  /*
   * This tab already started an appearance. Keep it up on navigation and
   * refresh, and do not add another reminder count.
   * Beside the strip while that strip is on screen; compact once it is gone.
   */
  if (sessionActive) {
    return {
      visible: true,
      placement: progressBarVisible ? "beside" : "compact",
      record: null,
    };
  }

  // First time we actually see the onboarding or historical strip.
  if (progressBarVisible && !current.firstRunSeen) {
    return { visible: true, placement: "beside", record: "spotlight" };
  }

  /*
   * Later reminders: at most 3, and only after 7 days. A shop that never
   * had a progress-bar first run (lastShownAt still empty) can show now.
   * If the strip is up again, keep the card on that row instead of stacking
   * a second bar underneath it.
   */
  if (current.reminderCount < 3 && reminderGapElapsed(current.lastShownAt, now)) {
    return {
      visible: true,
      placement: progressBarVisible ? "beside" : "compact",
      record: "compact",
    };
  }

  return hidden();
};

/*
 * Writes the appearance once per tab. A second call in the same tab
 * (refresh, strict-mode effect, or the compact slot mounting too) no-ops.
 *
 * spotlight → firstRunSeen + lastShownAt, reminderCount unchanged.
 * compact   → reminderCount + 1 and lastShownAt.
 *
 * Example: recordPinAppBannerAppearance("shop.myshopify.com", "spotlight")
 *          then a second call returns false and leaves reminderCount at 0.
 */
export const recordPinAppBannerAppearance = (
  shopName,
  kind,
  now = Date.now(),
  local = defaultLocalStorage(),
  session = defaultSessionStorage()
) => {
  if (!shopName || (kind !== "spotlight" && kind !== "compact")) return false;
  if (isPinAppBannerSessionActive(shopName, session)) return false;

  const current = readPinAppBannerState(shopName, local);
  if (current.pinned || current.dismissCount >= 2) return false;

  const next = {
    ...current,
    lastShownAt: new Date(now).toISOString(),
  };

  if (kind === "spotlight") {
    next.firstRunSeen = true;
  } else {
    next.reminderCount = current.reminderCount + 1;
  }

  writePinAppBannerState(shopName, next, local);
  const sessionSaved = safeSet(session, sessionKey(shopName), "1");
  return sessionSaved;
};

export const dismissPinAppBanner = (
  shopName,
  local = defaultLocalStorage(),
  session = defaultSessionStorage()
) => {
  if (!shopName) return;
  const current = readPinAppBannerState(shopName, local);
  writePinAppBannerState(
    shopName,
    { ...current, dismissCount: current.dismissCount + 1 },
    local
  );
  // X hides the card. It does not mean the merchant pinned the app.
  clearPinAppBannerSession(shopName, session);
};

export const markPinAppBannerPinned = (
  shopName,
  local = defaultLocalStorage(),
  session = defaultSessionStorage()
) => {
  if (!shopName) return;
  const current = readPinAppBannerState(shopName, local);
  writePinAppBannerState(shopName, { ...current, pinned: true }, local);
  clearPinAppBannerSession(shopName, session);
};
